import React, { useEffect, useRef, useState } from 'react';
import { useApp } from '../context/AppContext';
import { get, post, patch, del, API } from '../lib/api';
import axios from 'axios';
import TopBar from '../components/layout/TopBar';
import SectionCard from '../components/widgets/SectionCard';
import KpiCard from '../components/widgets/KpiCard';
import EmptyState from '../components/widgets/EmptyState';
import { fmtBRL, fmtDate, todayISO, competenceLabel } from '../lib/format';
import {
  PhoneCall, Clock, AlertCircle, AlertTriangle, CheckCircle2, Plus, MessageSquare,
  Phone, Mail, Users, FileText, ChevronRight, Trash2, X, Upload, Download, ArrowDownToLine,
  DollarSign, Calendar, AlertOctagon, TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const CONTACT_TYPES = [
  { value: 'WHATSAPP', label: 'WhatsApp', icon: MessageSquare, color: '#25D366' },
  { value: 'TELEFONE', label: 'Telefone', icon: Phone, color: '#3B82F6' },
  { value: 'EMAIL', label: 'E-mail', icon: Mail, color: '#F59E0B' },
  { value: 'REUNIAO', label: 'Reunião', icon: Users, color: '#8B5CF6' },
  { value: 'VISITA', label: 'Visita', icon: Users, color: '#EC4899' },
  { value: 'OUTRO', label: 'Outro', icon: FileText, color: '#71717A' },
];

const STATUS_LABELS = {
  EM_NEGOCIACAO: { label: 'Em negociação', cls: 'pill-warning' },
  PROMESSA_PAGAMENTO: { label: 'Promessa de pagamento', cls: 'pill-gold' },
  SEM_RESPOSTA: { label: 'Sem resposta', cls: 'pill-muted' },
  PAGO: { label: 'Pago', cls: 'pill-success' },
  RENEGOCIADO: { label: 'Renegociado', cls: 'pill-gold' },
  BAIXA: { label: 'Baixa', cls: 'pill-danger' },
};

const BUCKET_STYLES = {
  em_dia: { accent: '#10B981', icon: CheckCircle2, badge: 'pill-success' },
  atraso_1_30: { accent: '#F59E0B', icon: Clock, badge: 'pill-warning' },
  atraso_31_60: { accent: '#EF4444', icon: AlertCircle, badge: 'pill-danger' },
  atraso_60_plus: { accent: '#DC2626', icon: AlertTriangle, badge: 'pill-danger' },
};

export default function Cobranca() {
  const { companies, companyFilter, competence } = useApp();
  const [aging, setAging] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [logs, setLogs] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [filterBucket, setFilterBucket] = useState('all');
  const [logForm, setLogForm] = useState({
    contact_date: todayISO(),
    contact_type: 'WHATSAPP',
    contacted_by: '',
    notes: '',
    status: 'EM_NEGOCIACAO',
    next_action_date: '',
    next_action_note: '',
  });

  const loadAging = async () => {
    try {
      setLoadError(null);
      const cid = companyFilter === 'all' ? null : companyFilter;
      const data = await get('/receivables/aging', cid ? { company_id: cid } : {});
      setAging(data);
    } catch (e) {
      console.error('[Cobranca] Falha ao carregar aging:', e);
      setLoadError(e.response?.data?.detail || e.message || 'Falha ao carregar dados');
      setAging({
        today: new Date().toISOString().slice(0, 10),
        buckets: {
          em_dia: { label: 'Em dia (a vencer)', items: [], total: 0 },
          atraso_1_30: { label: '1 a 30 dias', items: [], total: 0 },
          atraso_31_60: { label: '31 a 60 dias', items: [], total: 0 },
          atraso_60_plus: { label: '60+ dias (CRÍTICO)', items: [], total: 0 },
        },
        grand_total: 0,
        critical_total: 0,
      });
    }
  };

  // KPIs adicionais (recebido, cobranças, fundo perdido) + agenda
  const [extraKpis, setExtraKpis] = useState(null);
  const [agenda, setAgenda] = useState([]);
  const [drillOpen, setDrillOpen] = useState(null);  // 'received' | 'collections' | 'lost'
  const [drillItems, setDrillItems] = useState([]);

  const loadExtras = async () => {
    try {
      const cid = companyFilter === 'all' ? null : companyFilter;
      const params = cid ? { company_id: cid } : {};
      const [kpis, ag] = await Promise.all([
        get('/cobranca/dashboard', { competence, ...params }),
        get('/cobranca/agenda', { ...params, days_ahead: 30 }),
      ]);
      setExtraKpis(kpis);
      setAgenda(ag);
    } catch (e) {
      console.error('[Cobranca] Falha ao carregar extras:', e);
    }
  };

  useEffect(() => { loadAging(); loadExtras(); }, [companyFilter, competence]);

  const openDrill = async (type) => {
    setDrillOpen(type);
    setDrillItems([]);
    const cid = companyFilter === 'all' ? null : companyFilter;
    const params = cid ? { company_id: cid } : {};
    try {
      let url;
      if (type === 'received') url = `/cobranca/received-list?competence=${competence}`;
      else if (type === 'collections') url = `/cobranca/collections-list?competence=${competence}`;
      else if (type === 'lost') url = `/cobranca/lost-list`;
      const data = await get(url, params);
      setDrillItems(data);
    } catch (e) {
      toast.error('Erro ao carregar detalhamento');
    }
  };

  const openDrawer = async (rec) => {
    setSelected(rec);
    const summary = await get(`/receivables/${rec.id}/collection-summary`);
    setLogs(summary.logs || []);
    setDrawerOpen(true);
  };

  const openNewLog = (rec) => {
    setSelected(rec);
    setLogForm({
      contact_date: todayISO(),
      contact_type: 'WHATSAPP',
      contacted_by: '',
      notes: '',
      status: 'EM_NEGOCIACAO',
      next_action_date: '',
      next_action_note: '',
    });
    setLogOpen(true);
  };

  const saveLog = async () => {
    if (!logForm.notes.trim()) return toast.error('Descreva o contato');
    try {
      await post('/collection-logs', {
        ...logForm,
        receivable_id: selected.id,
        contacted_by: logForm.contacted_by || null,
        next_action_date: logForm.next_action_date || null,
        next_action_note: logForm.next_action_note || null,
      });
      toast.success('Cobrança registrada');
      setLogOpen(false);
      await loadAging();
      // refresh drawer logs
      if (drawerOpen) {
        const summary = await get(`/receivables/${selected.id}/collection-summary`);
        setLogs(summary.logs || []);
      }
    } catch (e) {
      toast.error('Erro ao registrar');
    }
  };

  const removeLog = async (lid) => {
    if (!window.confirm('Excluir este registro?')) return;
    await del(`/collection-logs/${lid}`);
    const summary = await get(`/receivables/${selected.id}/collection-summary`);
    setLogs(summary.logs || []);
  };

  // ----- Import / Novo Recebível -----
  const fileRef = useRef(null);
  const [importBusy, setImportBusy] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState({
    company_id: '', client: '', description: '', amount: '',
    due_date: todayISO(), invoice_date: '', organ: '', municipality: '',
  });

  const downloadTemplate = () => {
    window.open(`${API}/receivables/template`, '_blank');
    toast.success('Template baixado');
  };

  const onImportClick = () => fileRef.current?.click();

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImportBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('replace', 'false');
      const { data } = await axios.post(`${API}/receivables/import`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Importado: ${data.created} recebíveis criados (${data.skipped} ignorados)`);
      if (data.errors?.length) {
        console.warn('Linhas com erro:', data.errors);
      }
      await loadAging();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao importar');
    } finally {
      setImportBusy(false);
    }
  };

  const createReceivable = async () => {
    if (!newForm.company_id || !newForm.client.trim()) return toast.error('Empresa e cliente obrigatórios');
    const amount = parseFloat(String(newForm.amount).replace(',', '.'));
    if (!amount || amount <= 0) return toast.error('Valor inválido');
    try {
      await post('/receivables', {
        ...newForm,
        amount,
        invoice_date: newForm.invoice_date || null,
        organ: newForm.organ || null,
        municipality: newForm.municipality || null,
      });
      toast.success('Recebível criado');
      setNewOpen(false);
      setNewForm({
        company_id: '', client: '', description: '', amount: '',
        due_date: todayISO(), invoice_date: '', organ: '', municipality: '',
      });
      await loadAging();
    } catch (e) {
      toast.error('Erro ao criar');
    }
  };

  // Item 8: modal de escolha de data ao marcar como pago
  const [paidDialogOpen, setPaidDialogOpen] = useState(false);
  const [paidTarget, setPaidTarget] = useState(null);
  const [paidDate, setPaidDate] = useState(todayISO());

  const markAsPaid = (rec) => {
    setPaidTarget(rec);
    setPaidDate(todayISO());
    setPaidDialogOpen(true);
  };

  const confirmMarkPaid = async () => {
    if (!paidDate) return toast.error('Selecione a data de recebimento');
    try {
      await patch(`/receivables/${paidTarget.id}`, { status: 'PAGO', received_date: paidDate });
      toast.success(`Marcado como recebido em ${new Date(paidDate + 'T00:00').toLocaleDateString('pt-BR')}`);
      setPaidDialogOpen(false);
      loadAging();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    }
  };

  const removeReceivable = async (id) => {
    if (!window.confirm('Excluir este recebível? Histórico de cobrança será removido também.')) return;
    await del(`/receivables/${id}`);
    toast.success('Excluído');
    loadAging();
  };

  // ----- Edição de recebível -----
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const openEdit = (rec) => {
    setEditForm({
      id: rec.id,
      company_id: rec.company_id || '',
      client: rec.client || '',
      description: rec.description || '',
      amount: rec.amount ?? '',
      due_date: rec.due_date || '',
      invoice_date: rec.invoice_date || '',
      received_date: rec.received_date || '',
      status: rec.status || 'PENDENTE',
      organ: rec.organ || '',
      municipality: rec.municipality || '',
      category_id: rec.category_id || '',
      notes: rec.notes || '',
    });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    if (!editForm.description?.trim()) return toast.error('Descrição obrigatória');
    if (!editForm.amount || Number(editForm.amount) <= 0) return toast.error('Valor obrigatório');
    if (!editForm.due_date) return toast.error('Vencimento obrigatório');
    if (!editForm.company_id) return toast.error('Selecione a empresa');
    try {
      const payload = {
        company_id: editForm.company_id,
        client: editForm.client || null,
        description: editForm.description,
        amount: Number(editForm.amount),
        due_date: editForm.due_date,
        invoice_date: editForm.invoice_date || null,
        received_date: editForm.received_date || null,
        status: editForm.status,
        organ: editForm.organ || null,
        municipality: editForm.municipality || null,
        category_id: editForm.category_id || null,
        notes: editForm.notes || null,
      };
      await patch(`/receivables/${editForm.id}`, payload);
      toast.success('Cobrança atualizada');
      setEditOpen(false);
      await loadAging();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao atualizar');
    }
  };

  if (!aging) {
    return (
      <div className="fade-in">
        <TopBar
          title="Contas a Receber & Cobrança"
          subtitle="Aging analítico, gestão de recebíveis e mini CRM de cobrança em um só lugar"
        />
        <div className="px-8 py-7 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="kpi-card p-5 animate-pulse">
                <div className="h-3 w-32 bg-zinc-800 rounded mb-3" />
                <div className="h-7 w-40 bg-zinc-800 rounded" />
                <div className="h-3 w-24 bg-zinc-800 rounded mt-3" />
              </div>
            ))}
          </div>
          <div className="ceap-card p-12 text-center text-zinc-500 text-sm">
            Carregando recebíveis...
          </div>
        </div>
      </div>
    );
  }

  const allItems = Object.entries(aging.buckets).flatMap(([key, b]) =>
    b.items.map((i) => ({ ...i, _bucket: key }))
  );
  const filtered = filterBucket === 'all' ? allItems : allItems.filter((i) => i._bucket === filterBucket);

  return (
    <div className="fade-in">
      <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={onFileChange} style={{ display: 'none' }} data-testid="hidden-import-input" />
      <TopBar
        title="Contas a Receber & Cobrança"
        subtitle="Aging analítico, gestão de recebíveis e mini CRM de cobrança em um só lugar"
        action={
          <div className="flex gap-2">
            <Button
              onClick={downloadTemplate}
              variant="outline"
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-900 gap-1.5"
              size="sm"
              data-testid="btn-download-recv-template"
            >
              <Download size={14} /> Template
            </Button>
            <Button
              onClick={onImportClick}
              disabled={importBusy}
              variant="outline"
              className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 gap-1.5"
              size="sm"
              data-testid="btn-import-receivables"
            >
              <Upload size={14} /> {importBusy ? 'Importando...' : 'Importar Planilha'}
            </Button>
            <Button
              onClick={() => setNewOpen(true)}
              className="btn-gold gap-1.5"
              size="sm"
              data-testid="btn-new-receivable"
            >
              <Plus size={14} /> Novo Recebível
            </Button>
          </div>
        }
      />
      <div className="px-8 py-7 space-y-5">
        {loadError && (
          <div className="flex items-start gap-3 px-4 py-3 bg-red-950/30 border border-red-700/40 rounded-md">
            <AlertCircle size={16} className="text-red-400 mt-0.5 shrink-0" />
            <div className="text-[12px] text-red-200/80">
              <strong className="text-red-300">Aviso:</strong> {loadError}
            </div>
          </div>
        )}

        {/* AGING KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {Object.entries(aging.buckets).map(([key, b]) => {
            const s = BUCKET_STYLES[key];
            const Icon = s.icon;
            return (
              <button
                key={key}
                onClick={() => setFilterBucket(filterBucket === key ? 'all' : key)}
                className={`text-left transition-all ${filterBucket === key ? 'ring-2 ring-[#D4AF37] rounded-lg' : ''}`}
                data-testid={`aging-bucket-${key}`}
              >
                <KpiCard
                  label={b.label}
                  value={b.total}
                  accent={s.accent}
                  icon={Icon}
                  deltaLabel={`${b.items.length} recebíveis`}
                  highlight={key === 'atraso_60_plus' && b.total > 0}
                />
              </button>
            );
          })}
        </div>

        {/* KPIs Operacionais da Competência (clicáveis) */}
        {extraKpis && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button onClick={() => openDrill('received')} className="text-left" data-testid="kpi-received-period">
              <KpiCard
                label={`Recebido em ${competenceLabel(competence)}`}
                value={extraKpis.received_in_period.total}
                accent="#10B981"
                icon={DollarSign}
                deltaLabel={`${extraKpis.received_in_period.count} pagamento${extraKpis.received_in_period.count === 1 ? '' : 's'}`}
              />
            </button>
            <button onClick={() => openDrill('collections')} className="text-left" data-testid="kpi-collections-period">
              <KpiCard
                label={`Cobranças Realizadas em ${competenceLabel(competence)}`}
                value={extraKpis.collections_in_period.count}
                format="number"
                accent="#3B82F6"
                icon={PhoneCall}
                deltaLabel="clique para ver histórico"
              />
            </button>
            <button onClick={() => openDrill('lost')} className="text-left" data-testid="kpi-lost">
              <KpiCard
                label="Fundo Perdido (>17 meses)"
                value={extraKpis.lost.total}
                accent="#DC2626"
                icon={AlertOctagon}
                deltaLabel={`${extraKpis.lost.count} recebível${extraKpis.lost.count === 1 ? '' : 'is'} para baixa`}
                highlight={extraKpis.lost.count > 0}
              />
            </button>
          </div>
        )}

        {/* Agenda de Retornos */}
        {agenda.length > 0 && (
          <SectionCard
            title="Agenda de Retornos"
            subtitle={`${agenda.filter((a) => a.overdue).length} atrasado${agenda.filter((a) => a.overdue).length === 1 ? '' : 's'} · próximos 30 dias`}
            testid="cobranca-agenda"
          >
            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-2">
              {agenda.map((a, idx) => {
                const co = companies.find((c) => c.id === a.receivable.company_id);
                return (
                  <button
                    key={idx}
                    onClick={() => openDrawer(a.receivable)}
                    className={`w-full text-left flex items-center gap-3 px-3.5 py-3 rounded-md border transition-all ${a.overdue ? 'border-red-700/40 bg-red-950/20 hover:bg-red-950/30' : 'border-zinc-800 bg-zinc-900/30 hover:bg-zinc-900/50'}`}
                    data-testid={`agenda-item-${idx}`}
                  >
                    <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${a.overdue ? 'bg-red-950/40 border border-red-700/40' : 'bg-amber-950/30 border border-amber-700/40'}`}>
                      <Calendar size={15} className={a.overdue ? 'text-red-400' : 'text-amber-400'} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-zinc-100 font-medium text-sm truncate">{a.client}</span>
                        {a.overdue ? (
                          <span className="pill pill-danger text-[9px]">
                            ATRASADO {Math.abs(a.days_until)}d
                          </span>
                        ) : a.days_until === 0 ? (
                          <span className="pill pill-warning text-[9px]">HOJE</span>
                        ) : (
                          <span className="pill pill-info text-[9px]">em {a.days_until}d</span>
                        )}
                        {co && <span className="text-[10px] text-zinc-500">· {co.name}</span>}
                      </div>
                      <div className="text-[11px] text-zinc-500 flex items-center gap-3">
                        <span>Retorno: <strong className="text-zinc-300">{fmtDate(a.next_contact_date)}</strong></span>
                        <span>Vencimento: {fmtDate(a.due_date)}</span>
                        <span className="gold-text font-mono">{fmtBRL(a.amount)}</span>
                      </div>
                      {a.log.next_action_note && (
                        <div className="text-[11px] text-zinc-400 mt-1 italic line-clamp-1">"{a.log.next_action_note}"</div>
                      )}
                    </div>
                    <ChevronRight size={14} className="text-zinc-600 shrink-0" />
                  </button>
                );
              })}
            </div>
          </SectionCard>
        )}

        {filterBucket !== 'all' && (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-zinc-400">Filtro ativo:</span>
            <span className="pill pill-gold">{aging.buckets[filterBucket].label}</span>
            <button
              onClick={() => setFilterBucket('all')}
              className="text-zinc-500 hover:text-zinc-200 transition-colors"
              data-testid="btn-clear-filter"
            >
              <X size={12} /> remover filtro
            </button>
          </div>
        )}

        <SectionCard
          title="Carteira de Recebíveis"
          subtitle={`${filtered.length} de ${allItems.length} pendentes | Total: ${fmtBRL(filtered.reduce((s, x) => s + x.amount, 0))}`}
          padded={false}
          testid="aging-table"
        >
          {filtered.length === 0 ? (
            <div className="p-12">
              <EmptyState
                icon={CheckCircle2}
                title={allItems.length === 0 ? 'Nenhum recebível cadastrado' : 'Sem recebíveis nesta faixa'}
                description={allItems.length === 0 ? 'Importe sua planilha de cobrança ou cadastre manualmente para começar.' : 'Que ótimo! Não há cobranças nesta faixa de atraso.'}
                action={allItems.length === 0 ? (
                  <div className="flex gap-2 justify-center">
                    <Button onClick={downloadTemplate} variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-900 gap-1.5">
                      <Download size={14} /> Baixar Template
                    </Button>
                    <Button onClick={onImportClick} className="btn-gold gap-1.5">
                      <Upload size={14} /> Importar Planilha
                    </Button>
                    <Button onClick={() => setNewOpen(true)} variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-900 gap-1.5">
                      <Plus size={14} /> Cadastrar Manual
                    </Button>
                  </div>
                ) : null}
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full ceap-table">
                <thead>
                  <tr>
                    <th>Vencimento</th>
                    <th>Cliente / Órgão</th>
                    <th>Município</th>
                    <th>Curso / Descrição</th>
                    <th>Empresa</th>
                    <th className="text-center">Dias em Atraso</th>
                    <th className="text-right">Valor</th>
                    <th className="text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.sort((a, b) => b.days_overdue - a.days_overdue).map((r) => {
                    const co = companies.find((c) => c.id === r.company_id);
                    const s = BUCKET_STYLES[r._bucket];
                    return (
                      <tr key={r.id} data-testid={`recb-row-${r.id}`}>
                        <td className="font-mono text-zinc-300">{fmtDate(r.due_date)}</td>
                        <td className="text-zinc-100 font-medium">{r.organ || r.client}</td>
                        <td className="text-zinc-400 text-xs">{r.municipality || '—'}</td>
                        <td className="text-zinc-400 text-xs">{r.description}</td>
                        <td>
                          <div className="flex items-center gap-1.5">
                            <span className="w-1.5 h-4 rounded-sm" style={{ background: co?.color || '#888' }} />
                            <span className="text-zinc-300 text-xs">{co?.name || '-'}</span>
                          </div>
                        </td>
                        <td className="text-center">
                          {r.days_overdue > 0 ? (
                            <span className={`pill ${s.badge}`}>{r.days_overdue} dias</span>
                          ) : (
                            <span className="pill pill-success">Em dia</span>
                          )}
                        </td>
                        <td className="text-right font-mono emerald-text font-semibold">{fmtBRL(r.amount)}</td>
                        <td className="text-right">
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={() => openNewLog(r)}
                              className="px-2 py-1 rounded bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/20 transition-all text-xs gap-1 inline-flex items-center"
                              data-testid={`btn-cobranca-${r.id}`}
                            >
                              <PhoneCall size={11} /> Registrar
                            </button>
                            <button
                              onClick={() => openDrawer(r)}
                              className="px-2 py-1 rounded border border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-xs gap-1 inline-flex items-center"
                              data-testid={`btn-history-${r.id}`}
                            >
                              Histórico <ChevronRight size={11} />
                            </button>
                            <button
                              onClick={() => openEdit(r)}
                              className="p-1.5 rounded hover:bg-blue-900/30 text-zinc-400 hover:text-blue-400 text-xs"
                              title="Editar cobrança"
                              data-testid={`btn-edit-recv-${r.id}`}
                            >
                              <FileText size={12} />
                            </button>
                            <button
                              onClick={() => markAsPaid(r)}
                              className="p-1.5 rounded hover:bg-emerald-900/30 text-zinc-400 hover:text-emerald-400 text-xs"
                              title="Marcar como recebido"
                              data-testid={`btn-paid-${r.id}`}
                            >
                              <CheckCircle2 size={12} />
                            </button>
                            <button
                              onClick={() => removeReceivable(r.id)}
                              className="p-1.5 rounded hover:bg-red-900/30 text-zinc-400 hover:text-red-400 text-xs"
                              title="Excluir recebível"
                              data-testid={`btn-delete-recv-${r.id}`}
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      </div>

      {/* Item 8: DIALOG de data de recebimento */}
      <Dialog open={paidDialogOpen} onOpenChange={setPaidDialogOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-zinc-50">Marcar como Recebido</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Informe a data em que o pagamento foi <strong>efetivamente recebido</strong> em conta.
              O sistema lança essa data no Fluxo de Caixa Realizado.
            </DialogDescription>
          </DialogHeader>
          {paidTarget && (
            <div className="space-y-4 py-2">
              <div className="p-3 rounded bg-zinc-900/60 border border-zinc-800 text-xs">
                <div className="text-zinc-400">Recebível:</div>
                <div className="text-zinc-100 font-semibold mt-1">{paidTarget.description}</div>
                <div className="flex justify-between mt-1">
                  <span className="text-zinc-500">Cliente: {paidTarget.client || '—'}</span>
                  <span className="emerald-text font-mono font-bold">
                    R$ {(paidTarget.amount || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="text-zinc-500 mt-1">Vencimento: {paidTarget.due_date}</div>
              </div>
              <div>
                <Label className="text-zinc-300">Data do Recebimento *</Label>
                <Input
                  type="date"
                  value={paidDate}
                  onChange={(e) => setPaidDate(e.target.value)}
                  className="bg-zinc-900 border-zinc-700"
                  data-testid="paid-date-input"
                />
                <div className="text-[11px] text-zinc-500 mt-1">
                  Essa data será usada no Fluxo de Caixa Realizado (competência = mês da data).
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaidDialogOpen(false)} className="border-zinc-700 text-zinc-300" data-testid="paid-cancel-btn">
              Cancelar
            </Button>
            <Button onClick={confirmMarkPaid} className="btn-gold" data-testid="paid-confirm-btn">
              Confirmar Recebimento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODAL de edição de cobrança */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-zinc-50">Editar Cobrança</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Altere os dados do recebível. Status, valor, vencimento e empresa podem ser ajustados.
            </DialogDescription>
          </DialogHeader>
          {editForm && (
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="col-span-2">
                <Label className="text-zinc-300">Descrição</Label>
                <Input
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="bg-zinc-900 border-zinc-700"
                  data-testid="edit-recv-description"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Empresa</Label>
                <Select value={editForm.company_id} onValueChange={(v) => setEditForm({ ...editForm, company_id: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-700" data-testid="edit-recv-company">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-300">Cliente</Label>
                <Input
                  value={editForm.client}
                  onChange={(e) => setEditForm({ ...editForm, client: e.target.value })}
                  className="bg-zinc-900 border-zinc-700"
                  data-testid="edit-recv-client"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Valor (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editForm.amount}
                  onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                  className="bg-zinc-900 border-zinc-700"
                  data-testid="edit-recv-amount"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Vencimento</Label>
                <Input
                  type="date"
                  value={editForm.due_date}
                  onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })}
                  className="bg-zinc-900 border-zinc-700"
                  data-testid="edit-recv-due-date"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Data Emissão NF</Label>
                <Input
                  type="date"
                  value={editForm.invoice_date}
                  onChange={(e) => setEditForm({ ...editForm, invoice_date: e.target.value })}
                  className="bg-zinc-900 border-zinc-700"
                  data-testid="edit-recv-invoice-date"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Data Recebimento</Label>
                <Input
                  type="date"
                  value={editForm.received_date}
                  onChange={(e) => setEditForm({ ...editForm, received_date: e.target.value })}
                  className="bg-zinc-900 border-zinc-700"
                  data-testid="edit-recv-received-date"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Status</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-700" data-testid="edit-recv-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    <SelectItem value="PENDENTE">Pendente</SelectItem>
                    <SelectItem value="PAGO">Pago</SelectItem>
                    <SelectItem value="ATRASADO">Atrasado</SelectItem>
                    <SelectItem value="CANCELADO">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-300">Órgão</Label>
                <Input
                  value={editForm.organ}
                  onChange={(e) => setEditForm({ ...editForm, organ: e.target.value })}
                  className="bg-zinc-900 border-zinc-700"
                  data-testid="edit-recv-organ"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Município</Label>
                <Input
                  value={editForm.municipality}
                  onChange={(e) => setEditForm({ ...editForm, municipality: e.target.value })}
                  className="bg-zinc-900 border-zinc-700"
                  data-testid="edit-recv-municipality"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-zinc-300">Observações</Label>
                <Textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  rows={3}
                  className="bg-zinc-900 border-zinc-700"
                  data-testid="edit-recv-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="border-zinc-700 text-zinc-300" data-testid="btn-edit-recv-cancel">
              Cancelar
            </Button>
            <Button onClick={saveEdit} className="btn-gold" data-testid="btn-edit-recv-save">
              Salvar alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DRAWER de histórico */}
      <Dialog open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-zinc-50">
              Histórico de Cobrança
            </DialogTitle>
            {selected && (
              <div className="text-sm text-zinc-400 mt-1">
                <span className="text-zinc-100">{selected.client}</span> · {selected.description} · {fmtBRL(selected.amount)}
              </div>
            )}
          </DialogHeader>

          <div className="flex justify-end mb-3">
            <Button
              onClick={() => {
                // Fecha o drawer e aguarda animação de saída antes de abrir o outro dialog
                // (evita conflito de Portals do Radix causando removeChild error)
                const rec = selected;
                setDrawerOpen(false);
                setTimeout(() => openNewLog(rec), 250);
              }}
              className="btn-gold gap-1.5"
              size="sm"
              data-testid="btn-new-log-from-drawer"
            >
              <Plus size={13} /> Novo Contato
            </Button>
          </div>

          {logs.length === 0 ? (
            <div className="py-10 text-center text-zinc-500 text-sm">
              Nenhum registro de cobrança ainda.
            </div>
          ) : (
            <div className="space-y-3">
              {logs.map((l) => {
                const ct = CONTACT_TYPES.find((c) => c.value === l.contact_type) || CONTACT_TYPES[5];
                const Icon = ct.icon;
                const stat = STATUS_LABELS[l.status] || STATUS_LABELS.EM_NEGOCIACAO;
                return (
                  <div key={l.id} className="border border-zinc-800 rounded-md p-4 bg-zinc-900/40" data-testid={`log-${l.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-7 h-7 rounded-md flex items-center justify-center"
                          style={{ background: `${ct.color}20`, border: `1px solid ${ct.color}40` }}
                        >
                          <Icon size={13} style={{ color: ct.color }} />
                        </div>
                        <div>
                          <div className="text-sm text-zinc-100 font-medium">{ct.label}</div>
                          <div className="text-[11px] text-zinc-500 font-mono">
                            {fmtDate(l.contact_date)}
                            {l.contacted_by && ` · por ${l.contacted_by}`}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`pill ${stat.cls}`}>{stat.label}</span>
                        <button
                          onClick={() => removeLog(l.id)}
                          className="text-zinc-600 hover:text-red-400 p-1"
                          data-testid={`btn-delete-log-${l.id}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                    <div className="mt-3 text-sm text-zinc-300">{l.notes}</div>
                    {l.next_action_date && (
                      <div className="mt-2 pt-2 border-t border-zinc-800/60 flex items-center gap-2 text-xs">
                        <span className="text-zinc-500">Próxima ação:</span>
                        <span className="gold-text font-mono">{fmtDate(l.next_action_date)}</span>
                        {l.next_action_note && <span className="text-zinc-400">— {l.next_action_note}</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* DIALOG novo contato */}
      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-xl" data-testid="dialog-new-log">
          <DialogHeader>
            <DialogTitle className="font-display">Registrar Cobrança</DialogTitle>
            {selected && (
              <div className="text-xs text-zinc-500">
                <span className="text-zinc-300">{selected.client}</span> — {fmtBRL(selected.amount)} (venc. {fmtDate(selected.due_date)})
              </div>
            )}
          </DialogHeader>

          {/* Card com contato do responsável pelo pagamento (se existir) */}
          {selected && (selected.responsible_name || selected.responsible_email || selected.responsible_phone) && (
            <div className="px-3 py-2.5 bg-amber-950/20 border border-amber-700/40 rounded-md" data-testid="registrar-contact-card">
              <div className="text-[11px] uppercase tracking-wider text-amber-400/80 mb-1.5 font-medium">
                Contato do Responsável pelo Pagamento
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-1 text-[12.5px]">
                {selected.responsible_name && (
                  <div>
                    <span className="text-zinc-500 text-[10px] uppercase mr-1.5">Nome:</span>
                    <span className="text-zinc-100" data-testid="registrar-resp-name">{selected.responsible_name}</span>
                  </div>
                )}
                {selected.responsible_phone && (
                  <div>
                    <span className="text-zinc-500 text-[10px] uppercase mr-1.5">Tel:</span>
                    <a
                      href={`tel:${selected.responsible_phone.replace(/\D/g, '')}`}
                      className="text-zinc-100 hover:text-amber-300 font-mono"
                      data-testid="registrar-resp-phone"
                    >
                      {selected.responsible_phone}
                    </a>
                  </div>
                )}
                {selected.responsible_email && (
                  <div className="md:col-span-3">
                    <span className="text-zinc-500 text-[10px] uppercase mr-1.5">E-mail:</span>
                    <a
                      href={`mailto:${selected.responsible_email}`}
                      className="text-zinc-100 hover:text-amber-300 break-all"
                      data-testid="registrar-resp-email"
                    >
                      {selected.responsible_email}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Data do Contato</Label>
              <Input type="date" value={logForm.contact_date}
                onChange={(e) => setLogForm({ ...logForm, contact_date: e.target.value })}
                className="bg-zinc-900 border-zinc-800 mt-1 font-mono" data-testid="log-contact-date" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Canal</Label>
              <Select value={logForm.contact_type} onValueChange={(v) => setLogForm({ ...logForm, contact_type: v })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800 mt-1" data-testid="log-contact-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                  {CONTACT_TYPES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Responsável (quem ligou)</Label>
              <Input value={logForm.contacted_by}
                onChange={(e) => setLogForm({ ...logForm, contacted_by: e.target.value })}
                placeholder="Nome do operador"
                className="bg-zinc-900 border-zinc-800 mt-1" data-testid="log-contacted-by" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Status</Label>
              <Select value={logForm.status} onValueChange={(v) => setLogForm({ ...logForm, status: v })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800 mt-1" data-testid="log-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                  {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Anotações do Contato *</Label>
              <Textarea value={logForm.notes}
                onChange={(e) => setLogForm({ ...logForm, notes: e.target.value })}
                rows={3} placeholder="Ex: Falei com a Maria. Ela vai pagar até dia 25."
                className="bg-zinc-900 border-zinc-800 mt-1" data-testid="log-notes" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Próxima Ação (data)</Label>
              <Input type="date" value={logForm.next_action_date}
                onChange={(e) => setLogForm({ ...logForm, next_action_date: e.target.value })}
                className="bg-zinc-900 border-zinc-800 mt-1 font-mono" data-testid="log-next-action-date" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Próxima Ação (descrição)</Label>
              <Input value={logForm.next_action_note}
                onChange={(e) => setLogForm({ ...logForm, next_action_note: e.target.value })}
                placeholder="Ex: Reforçar via WhatsApp"
                className="bg-zinc-900 border-zinc-800 mt-1" data-testid="log-next-action-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLogOpen(false)} className="text-zinc-400 hover:bg-zinc-900">Cancelar</Button>
            <Button onClick={saveLog} className="btn-gold" data-testid="btn-save-log">Registrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* DIALOG novo recebível manual */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl" data-testid="dialog-new-recv">
          <DialogHeader>
            <DialogTitle className="font-display">Novo Recebível</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Empresa *</Label>
              <Select value={newForm.company_id} onValueChange={(v) => setNewForm({ ...newForm, company_id: v })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800 mt-1" data-testid="new-recv-company"><SelectValue placeholder="Empresa" /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Cliente / Órgão *</Label>
              <Input value={newForm.client} onChange={(e) => setNewForm({ ...newForm, client: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1" placeholder="Ex: PREF. TIJUCAS" data-testid="new-recv-client" />
            </div>
            <div className="col-span-2">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Descrição / Curso ou Evento</Label>
              <Input value={newForm.description} onChange={(e) => setNewForm({ ...newForm, description: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1" placeholder="Ex: SC LICITAÇÕES MAR/2026" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Município</Label>
              <Input value={newForm.municipality} onChange={(e) => setNewForm({ ...newForm, municipality: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1" placeholder="Tijucas/SC" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Órgão</Label>
              <Input value={newForm.organ} onChange={(e) => setNewForm({ ...newForm, organ: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1" placeholder="Prefeitura, Câmara..." />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Valor *</Label>
              <Input value={newForm.amount} onChange={(e) => setNewForm({ ...newForm, amount: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1 font-mono" placeholder="0,00" data-testid="new-recv-amount" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Data da NF</Label>
              <Input type="date" value={newForm.invoice_date} onChange={(e) => setNewForm({ ...newForm, invoice_date: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1 font-mono" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Vencimento *</Label>
              <Input type="date" value={newForm.due_date} onChange={(e) => setNewForm({ ...newForm, due_date: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1 font-mono" data-testid="new-recv-due" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNewOpen(false)} className="text-zinc-400 hover:bg-zinc-900">Cancelar</Button>
            <Button onClick={createReceivable} className="btn-gold" data-testid="btn-save-new-recv">Cadastrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DRILL-DOWN DIALOG (Recebido | Cobranças | Fundo Perdido) */}
      <Dialog open={!!drillOpen} onOpenChange={(v) => !v && setDrillOpen(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="dialog-cobranca-drill">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              {drillOpen === 'received' && <><DollarSign size={18} className="text-emerald-400" /> Recebido em {competenceLabel(competence)}</>}
              {drillOpen === 'collections' && <><PhoneCall size={18} className="text-blue-400" /> Cobranças Realizadas em {competenceLabel(competence)}</>}
              {drillOpen === 'lost' && <><AlertOctagon size={18} className="text-red-400" /> Fundo Perdido (vencidos há mais de 17 meses)</>}
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              {drillItems.length} item{drillItems.length === 1 ? '' : 's'}
            </DialogDescription>
          </DialogHeader>

          {drillItems.length === 0 ? (
            <div className="text-center py-10 text-zinc-500 text-sm">Sem registros para exibir</div>
          ) : drillOpen === 'received' ? (
            <table className="w-full ceap-table" data-testid="drill-received-table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Curso/Descrição</th>
                  <th>Vencimento</th>
                  <th>Data Recebimento</th>
                  <th className="text-right">Valor</th>
                </tr>
              </thead>
              <tbody>
                {drillItems.map((r) => (
                  <tr key={r.id}>
                    <td className="text-zinc-100">{r.client || r.organ}</td>
                    <td className="text-zinc-300 text-xs">{r.description}</td>
                    <td className="font-mono text-xs text-zinc-400">{fmtDate(r.due_date)}</td>
                    <td className="font-mono text-xs emerald-text">{fmtDate(r.received_date)}</td>
                    <td className="text-right font-mono emerald-text">{fmtBRL(r.amount)}</td>
                  </tr>
                ))}
                <tr className="bg-zinc-900/40 border-t-2 border-emerald-700/40">
                  <td colSpan={4} className="text-right text-zinc-100 font-medium">TOTAL</td>
                  <td className="text-right font-mono font-bold emerald-text">
                    {fmtBRL(drillItems.reduce((s, r) => s + Number(r.amount || 0), 0))}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : drillOpen === 'collections' ? (
            <div className="space-y-2.5">
              {drillItems.map((lo) => (
                <div key={lo.id} className="border border-zinc-800 rounded-md p-3 bg-zinc-900/30">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-zinc-100 font-medium">{lo.client}</span>
                      <span className="text-[10px] text-zinc-500 font-mono">· {lo.contact_type}</span>
                      <span className="text-[10px] text-zinc-500">por <strong className="text-zinc-300">{lo.contacted_by || '—'}</strong></span>
                    </div>
                    <span className="font-mono gold-text text-sm">{fmtBRL(lo.amount)}</span>
                  </div>
                  <div className="text-[11px] text-zinc-400 mb-1">
                    <strong>{fmtDate(lo.contact_date)}</strong> · vencimento {fmtDate(lo.due_date)} · status: <span className="text-amber-400">{lo.status}</span>
                  </div>
                  {lo.notes && <div className="text-[12px] text-zinc-300 italic mt-1">"{lo.notes}"</div>}
                  {lo.next_action_date && (
                    <div className="text-[11px] text-blue-400 mt-1">→ Próximo retorno: {fmtDate(lo.next_action_date)} {lo.next_action_note && `· ${lo.next_action_note}`}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <table className="w-full ceap-table" data-testid="drill-lost-table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Descrição</th>
                  <th>Vencimento</th>
                  <th className="text-center">Dias em Atraso</th>
                  <th className="text-right">Valor</th>
                </tr>
              </thead>
              <tbody>
                {drillItems.map((r) => (
                  <tr key={r.id}>
                    <td className="text-zinc-100">{r.client || r.organ}</td>
                    <td className="text-zinc-300 text-xs">{r.description}</td>
                    <td className="font-mono text-xs text-zinc-400">{fmtDate(r.due_date)}</td>
                    <td className="text-center font-mono text-red-400">{r.days_overdue}d</td>
                    <td className="text-right font-mono text-red-400">{fmtBRL(r.amount)}</td>
                  </tr>
                ))}
                <tr className="bg-zinc-900/40 border-t-2 border-red-700/40">
                  <td colSpan={4} className="text-right text-zinc-100 font-medium">TOTAL FUNDO PERDIDO</td>
                  <td className="text-right font-mono font-bold text-red-400">
                    {fmtBRL(drillItems.reduce((s, r) => s + Number(r.amount || 0), 0))}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
